import React, { useState, useEffect, useRef } from 'react';
import { 
  Calendar, Plus, Search, Edit2, Trash2, Loader2, X, Eye, EyeOff,
  MapPin, Clock, Upload, Image, Sparkles, BookOpen, Music, Trophy, Heart, MoreHorizontal
} from 'lucide-react';
import { eventsAPI } from '../services/api';
import Pagination from '../components/Pagination';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const CATEGORY_CONFIG = {
  spiritual: { label: "Ma'naviy tadbir", icon: Sparkles, color: '#8b5cf6', bg: '#ede9fe' },
  educational: { label: "Ma'rifiy tadbir", icon: BookOpen, color: '#3b82f6', bg: '#dbeafe' },
  cultural: { label: "Madaniy tadbir", icon: Music, color: '#ec4899', bg: '#fce7f3' },
  sport: { label: "Sport tadbiri", icon: Trophy, color: '#22c55e', bg: '#dcfce7' },
  volunteer: { label: "Volontyorlik", icon: Heart, color: '#f59e0b', bg: '#fef3c7' },
  other: { label: "Boshqa", icon: MoreHorizontal, color: '#64748b', bg: '#f1f5f9' },
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'spiritual',
    event_date: '',
    location: '',
    is_published: true,
  });

  useEffect(() => {
    fetchEvents();
  }, [filterCategory]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory) params.category = filterCategory;
      
      const response = await eventsAPI.getAll(params);
      setEvents(response.data.events || []);
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('content', formData.content);
      submitData.append('category', formData.category);
      submitData.append('is_published', formData.is_published);
      
      if (formData.event_date) {
        submitData.append('event_date', formData.event_date);
      }
      if (formData.location) {
        submitData.append('location', formData.location);
      }
      if (imageFile) {
        submitData.append('image', imageFile);
      }

      if (editingEvent) {
        await eventsAPI.update(editingEvent.id, submitData);
      } else {
        await eventsAPI.create(submitData);
      }
      setShowModal(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error('Xato:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Xatolik yuz berdi');
    }
  };

  const handleDelete = async (event) => {
    if (window.confirm(`"${event.title}" tadbirini o'chirmoqchimisiz?`)) {
      try {
        await eventsAPI.delete(event.id);
        fetchEvents();
      } catch (error) {
        alert('Xatolik yuz berdi');
      }
    }
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      content: event.content,
      category: event.category,
      event_date: event.event_date || '',
      location: event.location || '',
      is_published: event.is_published,
    });
    if (event.image) {
      setImagePreview(getImageUrl(event.image));
    } else {
      setImagePreview(null);
    }
    setImageFile(null);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingEvent(null);
    setFormData({
      title: '',
      content: '',
      category: 'spiritual',
      event_date: '',
      location: '',
      is_published: true,
    });
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredEvents = events.filter(e =>
    e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Calendar size={28} /> Ma'naviy-ma'rifiy tadbirlar</h1>
          <p>Tadbirlarni boshqarish va nashr qilish</p>
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={18} />
          Yangi tadbir
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="events-stats-grid">
          <div className="stat-card total">
            <div className="stat-icon"><Calendar size={24} /></div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">Jami tadbirlar</span>
            </div>
          </div>
          {Object.entries(CATEGORY_CONFIG).slice(0, 4).map(([key, config]) => {
            const Icon = config.icon;
            const count = stats.by_category?.[key]?.count || 0;
            return (
              <div key={key} className="stat-card" style={{ '--stat-color': config.color, '--stat-bg': config.bg }}>
                <div className="stat-icon" style={{ background: config.bg, color: config.color }}><Icon size={24} /></div>
                <div className="stat-info">
                  <span className="stat-value">{count}</span>
                  <span className="stat-label">{config.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="filter-select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="">Barcha kategoriyalar</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Tadbirlar ro'yxati */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} />
          <p>Tadbirlar topilmadi</p>
        </div>
      ) : (
        <div className="events-grid">
          {filteredEvents.map(event => {
            const catConfig = CATEGORY_CONFIG[event.category] || CATEGORY_CONFIG.other;
            const CatIcon = catConfig.icon;
            return (
              <div key={event.id} className={`event-card ${!event.is_published ? 'unpublished' : ''}`}>
                <div className="event-image">
                  {event.image ? (
                    <img src={getImageUrl(event.image)} alt={event.title} />
                  ) : (
                    <div className="no-image">
                      <CatIcon size={48} />
                    </div>
                  )}
                  <div className="event-category-badge" style={{ background: catConfig.bg, color: catConfig.color }}>
                    <CatIcon size={14} />
                    {catConfig.label}
                  </div>
                  {!event.is_published && (
                    <div className="unpublished-badge">
                      <EyeOff size={14} />
                      Qoralama
                    </div>
                  )}
                </div>
                <div className="event-content">
                  <h3>{event.title}</h3>
                  <p className="event-excerpt">{event.content.substring(0, 120)}...</p>
                  <div className="event-meta">
                    {event.event_date && (
                      <span><Clock size={14} /> {new Date(event.event_date).toLocaleDateString('uz-UZ')}</span>
                    )}
                    {event.location && (
                      <span><MapPin size={14} /> {event.location}</span>
                    )}
                    <span><Eye size={14} /> {event.views_count}</span>
                  </div>
                </div>
                <div className="event-actions">
                  <button className="btn btn-icon" onClick={() => openEditModal(event)} title="Tahrirlash">
                    <Edit2 size={18} />
                  </button>
                  <button className="btn btn-icon btn-danger" onClick={() => handleDelete(event)} title="O'chirish">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Tadbirni tahrirlash' : 'Yangi tadbir'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Rasm */}
                <div className="form-group">
                  <label>Rasm</label>
                  <div className={`event-image-upload ${imagePreview ? 'has-image' : ''}`}>
                    {imagePreview ? (
                      <img src={imagePreview} alt="Preview" />
                    ) : (
                      <div className="upload-placeholder">
                        <Image size={48} />
                        <span>Rasm yuklash</span>
                      </div>
                    )}
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-actions">
                      <button type="button" className="btn btn-sm" onClick={() => fileInputRef.current?.click()}>
                        <Upload size={16} />
                        {imagePreview ? "O'zgartirish" : 'Yuklash'}
                      </button>
                      {imagePreview && (
                        <button type="button" className="btn btn-sm btn-danger" onClick={handleImageRemove}>
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Sarlavha *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Tadbir sarlavhasi"
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Kategoriya *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tadbir sanasi</label>
                    <input
                      type="date"
                      value={formData.event_date}
                      onChange={(e) => setFormData({...formData, event_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Joy</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder="Tadbir o'tkaziladigan joy"
                  />
                </div>

                <div className="form-group">
                  <label>Matn *</label>
                  <textarea
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    placeholder="Tadbir haqida batafsil ma'lumot..."
                    rows={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                    />
                    <span>Nashr qilish (talabalar ko'rsin)</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingEvent ? 'Saqlash' : 'Qo\'shish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
